import { AppType } from 'app/console-api/apps/apps';
import { Offer, TradeType } from 'app/console-api/offers/offers';
import { PaginationResponseType } from 'app/console-api';
import { Pagination } from 'app/organizations-api/types';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN } from 'app/constants';

export interface MarketplaceApp {
  id: string;
  name: string;
  description: string;
  type: AppType;
  categories: string[];
  mau: number;
  dau: number;
}

export interface MarketplaceData {
  app: MarketplaceApp;
  tradeTypes: TradeType[];
  activeOffers: number;
  lastUpdated: Date;
}

export type MarketplaceSortBy = 'active_offers' | 'last_updated';

export interface MarketplaceFilters {
  name?: string;
  tradeTypes?: TradeType[];
  categories?: string[];
  mauMin?: number;
  mauMax?: number;
  sortBy?: MarketplaceSortBy;
}

export interface MarketplaceAppOffersFilters {
  plugins?: number[];
  deal_types?: TradeType[];
}

export const getMarketplaceApps = async (
  pagination: Pagination,
  filters?: MarketplaceFilters
): Promise<PaginationResponseType<MarketplaceData>> => {
  const token = localStorage.getItem(AUTH_TOKEN) || '';
  const searchParams = new URLSearchParams();
  searchParams.append('pageSize', pagination.pageSize.toString());
  searchParams.append('currentPage', pagination.currentPage.toString());
  if (filters?.name) {
    searchParams.append('name', filters.name);
  }
  if (filters?.tradeTypes?.length) {
    filters.tradeTypes.forEach((t) => searchParams.append('tradeTypes', t));
  }
  if (filters?.categories?.length) {
    filters.categories.forEach((c) => searchParams.append('categories', c));
  }
  if (filters?.mauMin !== undefined) {
    searchParams.append('mauMin', String(filters.mauMin));
  }
  if (filters?.mauMax !== undefined) {
    searchParams.append('mauMax', String(filters.mauMax));
  }
  if (filters?.sortBy) {
    searchParams.append('sortBy', filters.sortBy);
  }
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/marketplace/apps?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get marketplace apps: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const getMarketplaceAppOffers = async (
  appId: string,
  pagination: Pagination,
  filters?: MarketplaceAppOffersFilters
): Promise<PaginationResponseType<Offer>> => {
  const token = localStorage.getItem(AUTH_TOKEN) || '';
  const searchParams = new URLSearchParams();
  searchParams.append('pageSize', pagination.pageSize.toString());
  searchParams.append('currentPage', pagination.currentPage.toString());
  if (filters?.plugins?.length) {
    searchParams.append('plugins', filters.plugins.join(','));
  }
  if (filters?.deal_types?.length) {
    searchParams.append('deal_types', filters.deal_types.join(','));
  }
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/marketplace/apps/${appId}/offers?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get marketplace app offers: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};
